import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, ImagePlus, RotateCcw, Save, Trash2, X } from 'lucide-react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import WallpaperPreview from './WallpaperPreview.js';
import {
  CUSTOM_DEVICE_ID,
  DEFAULT_LAYOUT,
  DEVICES,
  LOGO_MAX_W,
  LOGO_MIN_W,
  QR_MAX_SIZE,
  QR_MIN_SIZE,
  autoQrColor,
  canvasToBlob,
  detectDevice,
  downloadBlob,
  exportQr,
  loadImage,
  normalizeLayout,
  renderWallpaper,
  type QrFormat,
  type WallpaperLayout,
} from '../lib/qrWallpaper.js';

interface Props {
  /** Full public URL of the card; this is what the QR code encodes. */
  url: string;
  /** Used for file names. */
  name: string;
  /** The logo stored on the card, preselected when present. */
  logoUrl?: string | null;
  /** Saved layout of this card (position, size, colours). */
  initialLayout?: unknown;
  /** Persists the layout on the card; rejects on failure. */
  onSave?: (layout: WallpaperLayout) => Promise<void>;
  onClose: () => void;
}

const QR_SIZES = [512, 1024, 2048, 4096];
const FORMATS: QrFormat[] = ['png', 'jpg', 'svg'];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function safeName(s: string): string {
  return s.normalize('NFKD').replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'karte';
}

/** Designs a phone lock-screen wallpaper (logo + QR code on a colour) and exports the plain QR code. */
export default function QrWallpaperDialog({ url, name, logoUrl, initialLayout, onSave, onClose }: Props) {
  const { t } = useAppTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const detected = useMemo(() => detectDevice(), []);
  const [deviceId, setDeviceId] = useState(detected ? (detected.id ?? CUSTOM_DEVICE_ID) : 'iphone-16');
  const [customW, setCustomW] = useState(detected && !detected.id ? detected.width : 1080);
  const [customH, setCustomH] = useState(detected && !detected.id ? detected.height : 2400);
  const [autoDetected, setAutoDetected] = useState(Boolean(detected));
  const [layout, setLayout] = useState<WallpaperLayout>(() => normalizeLayout(initialLayout));
  const savedJson = useRef(JSON.stringify(layout));
  const [showTemplate, setShowTemplate] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saved' | 'error'>('idle');
  const coarse = useMemo(() => typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches, []);
  const background = layout.background;
  const setBackground = (v: string) => setLayout((l) => ({ ...l, background: v }));
  const qrOverride = layout.qrColor;
  const setQrOverride = (v: string | null) => setLayout((l) => ({ ...l, qrColor: v }));
  const dirty = JSON.stringify(layout) !== savedJson.current;
  const [logo, setLogo] = useState<HTMLImageElement | null>(null);
  const [logoError, setLogoError] = useState(false);
  const [qrFormat, setQrFormat] = useState<QrFormat>('png');
  const [qrSize, setQrSize] = useState(1024);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (logoUrl) void loadStoredLogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoUrl]);

  async function loadStoredLogo() {
    if (!logoUrl) return;
    setLogoError(false);
    try { setLogo(await loadImage(logoUrl)); } catch { setLogoError(true); }
  }

  const qrColor = qrOverride ?? autoQrColor(background);
  const device = DEVICES.find((d) => d.id === deviceId);
  const width = device ? device.width : Math.min(Math.max(customW || 0, 200), 6000);
  const height = device ? device.height : Math.min(Math.max(customH || 0, 200), 6000);
  const file = safeName(name);

  useEffect(() => {
    const c = canvasRef.current;
    if (c) renderWallpaper(c, { width, height, layout, logo, url });
  }, [width, height, layout, logo, url]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const groups = useMemo(() => ({
    ios: DEVICES.filter((d) => d.platform === 'ios'),
    android: DEVICES.filter((d) => d.platform === 'android'),
  }), []);

  async function onLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setLogoError(false);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const img = await loadImage(String(reader.result));
        if (!img.naturalWidth) throw new Error('no size');
        setLogo(img);
      } catch {
        setLogoError(true);
      }
    };
    reader.readAsDataURL(f);
  }

  async function downloadWallpaper(type: 'image/png' | 'image/jpeg') {
    const c = canvasRef.current;
    if (!c) return;
    setBusy(true);
    try {
      const blob = await canvasToBlob(c, type);
      downloadBlob(blob, `${file}-hintergrund-${width}x${height}.${type === 'image/png' ? 'png' : 'jpg'}`);
    } finally {
      setBusy(false);
    }
  }

  async function saveLayout() {
    if (!onSave) return;
    setSaving(true);
    setSaveState('idle');
    try {
      await onSave(layout);
      savedJson.current = JSON.stringify(layout);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    } finally {
      setSaving(false);
    }
  }

  function resetPositions() {
    setLayout((l) => ({ ...l, logo: DEFAULT_LAYOUT.logo, qr: DEFAULT_LAYOUT.qr }));
  }

  async function downloadQr() {
    setBusy(true);
    try {
      const blob = await exportQr(url, qrFormat, qrSize, qrColor);
      downloadBlob(blob, `${file}-qr.${qrFormat}`);
    } finally {
      setBusy(false);
    }
  }

  const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1';
  const fieldCls = 'w-full rounded-lg border border-line bg-panel px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent-500';
  const btnCls = 'inline-flex items-center justify-center gap-2 rounded-lg bg-accent-600 px-3 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50 transition-colors';
  const btnGhost = 'inline-flex items-center justify-center gap-2 rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink hover:bg-raised disabled:opacity-50 transition-colors';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('qrWallpaper.title', 'QR-Hintergrund & QR-Code')}
        className="w-full max-w-4xl rounded-2xl bg-panel border border-line p-6 shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-ink">{t('qrWallpaper.title', 'QR-Hintergrund & QR-Code')}</h3>
          <button type="button" onClick={onClose} aria-label={t('common.close', 'Schließen')} className="rounded-lg p-1.5 text-ink-muted hover:bg-raised">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-5 text-sm text-ink-muted">
          {t('qrWallpaper.intro', 'Hintergrundbild für den Sperrbildschirm mit Logo und QR-Code, passend zur Handygröße. Der QR-Code führt auf die Karte.')}
        </p>

        <div className="grid grid-cols-[minmax(0,1fr)] gap-6 md:grid-cols-[minmax(0,1fr)_280px]">
          <div className="space-y-5">
            <section className="space-y-4">
              <div>
                <label htmlFor="qrw-device" className={labelCls}>{t('qrWallpaper.device', 'Handy-Modell')}</label>
                <select id="qrw-device" value={deviceId} onChange={(e) => { setDeviceId(e.target.value); setAutoDetected(false); }} className={fieldCls}>
                  <optgroup label="iPhone">
                    {groups.ios.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </optgroup>
                  <optgroup label="Android">
                    {groups.android.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </optgroup>
                  <option value={CUSTOM_DEVICE_ID}>{t('qrWallpaper.customSize', 'Eigene Größe …')}</option>
                </select>
                {autoDetected && (
                  <p className="mt-1 text-xs text-accent-600 dark:text-accent-400">
                    {t('qrWallpaper.autoDetected', 'Automatisch anhand dieses Geräts erkannt – du kannst es ändern.')}
                  </p>
                )}
                {device ? (
                  <p className="mt-1 text-xs text-ink-faint">{width} × {height} px</p>
                ) : (
                  <div className="mt-2 flex items-center gap-2">
                    <input type="number" min={200} max={6000} value={customW} onChange={(e) => { setCustomW(Number(e.target.value)); setAutoDetected(false); }} aria-label={t('qrWallpaper.width', 'Breite')} className={fieldCls} />
                    <span className="text-ink-faint">×</span>
                    <input type="number" min={200} max={6000} value={customH} onChange={(e) => { setCustomH(Number(e.target.value)); setAutoDetected(false); }} aria-label={t('qrWallpaper.height', 'Höhe')} className={fieldCls} />
                    <span className="text-xs text-ink-faint">px</span>
                  </div>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="qrw-bg" className={labelCls}>{t('qrWallpaper.background', 'Hintergrundfarbe')}</label>
                  <div className="flex items-center gap-3">
                    <input id="qrw-bg" type="color" value={background} onChange={(e) => setBackground(e.target.value)} className="h-10 w-14 cursor-pointer rounded-lg border border-line bg-transparent p-1" />
                    <input
                      type="text"
                      value={background}
                      maxLength={7}
                      onChange={(e) => { const v = e.target.value; if (HEX_RE.test(v)) setBackground(v.toLowerCase()); else if (/^#?[0-9a-fA-F]{0,6}$/.test(v)) setBackground(v); }}
                      aria-label={t('qrWallpaper.backgroundHex', 'Hex-Wert')}
                      className={`${fieldCls} font-mono`}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="qrw-qrcolor" className={labelCls}>{t('qrWallpaper.qrColor', 'QR-Farbe')}</label>
                  <div className="flex items-center gap-3">
                    <input id="qrw-qrcolor" type="color" value={qrColor} onChange={(e) => setQrOverride(e.target.value)} className="h-10 w-14 cursor-pointer rounded-lg border border-line bg-transparent p-1" />
                    {qrOverride ? (
                      <button type="button" onClick={() => setQrOverride(null)} className="text-xs font-medium text-accent-600 hover:underline dark:text-accent-400">
                        {t('qrWallpaper.qrColorAuto', 'Automatisch')}
                      </button>
                    ) : (
                      <span className="text-xs text-ink-faint">{t('qrWallpaper.qrColorAutoHint', 'Automatisch passend zum Hintergrund')}</span>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <span className={labelCls}>{t('qrWallpaper.logo', 'Logo')}</span>
                <div className="flex items-center gap-2">
                  <label className={`${btnGhost} cursor-pointer`}>
                    <ImagePlus className="h-4 w-4" aria-hidden="true" />
                    {logo ? t('qrWallpaper.logoChange', 'Logo ändern') : t('qrWallpaper.logoUpload', 'Logo hochladen')}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="sr-only" onChange={onLogo} />
                  </label>
                  {logoUrl && (
                    <button type="button" onClick={() => void loadStoredLogo()} className={btnGhost}>
                      {t('qrWallpaper.logoStored', 'Gespeichertes Logo der Karte')}
                    </button>
                  )}
                  {logo && (
                    <button type="button" onClick={() => setLogo(null)} className={btnGhost} aria-label={t('qrWallpaper.logoRemove', 'Logo entfernen')}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
                {logoError && <p className="mt-1 text-xs text-danger-500">{t('qrWallpaper.logoError', 'Das Logo konnte nicht gelesen werden. Bitte PNG, JPG, WebP oder SVG verwenden.')}</p>}
                <p className="mt-1 text-xs text-ink-faint">{t('qrWallpaper.logoHint', 'Am besten ein freigestelltes PNG oder SVG. Die Datei bleibt auf deinem Gerät.')}</p>
              </div>

              <div className="space-y-3 rounded-xl border border-line p-4">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-ink">{t('qrWallpaper.layout', 'Position & Größe')}</h4>
                  <button type="button" onClick={resetPositions} className="inline-flex items-center gap-1 text-xs font-medium text-accent-600 hover:underline dark:text-accent-400">
                    <RotateCcw className="h-3 w-3" aria-hidden="true" /> {t('qrWallpaper.resetLayout', 'Zurücksetzen')}
                  </button>
                </div>
                <p className="rounded-lg bg-accent-500/10 px-3 py-2 text-xs text-ink-muted">
                  {coarse
                    ? t('qrWallpaper.tipTouch', 'Tipp: Logo oder QR-Code in der Vorschau antippen und mit dem Finger verschieben. Zum Vergrößern und Verkleinern zwei Finger auf dem Element auseinander- oder zusammenziehen, oder den blauen Punkt unten rechts ziehen.')
                    : t('qrWallpaper.tipMouse', 'Tipp: Logo oder QR-Code in der Vorschau ziehen, um sie zu verschieben. Am blauen Punkt unten rechts ziehst du die Größe. Mit Pfeiltasten verschieben, mit + und − vergrößern.')}
                </p>
                {logo && (
                  <LayoutSliders
                    title={t('qrWallpaper.logo', 'Logo')}
                    size={layout.logo.w} min={LOGO_MIN_W} max={LOGO_MAX_W}
                    x={layout.logo.x} y={layout.logo.y}
                    onChange={(p) => setLayout((l) => ({ ...l, logo: { x: p.x ?? l.logo.x, y: p.y ?? l.logo.y, w: p.size ?? l.logo.w } }))}
                  />
                )}
                <LayoutSliders
                  title={t('qrWallpaper.qrCode', 'QR-Code')}
                  size={layout.qr.size} min={QR_MIN_SIZE} max={QR_MAX_SIZE}
                  x={layout.qr.x} y={layout.qr.y}
                  onChange={(p) => setLayout((l) => ({ ...l, qr: { x: p.x ?? l.qr.x, y: p.y ?? l.qr.y, size: p.size ?? l.qr.size } }))}
                />
                <label className="flex items-center gap-2 text-xs text-ink-muted">
                  <input type="checkbox" checked={showTemplate} onChange={(e) => setShowTemplate(e.target.checked)} className="h-4 w-4 rounded border-line accent-accent-600" />
                  {t('qrWallpaper.template', 'Sperrbildschirm-Schablone anzeigen (Uhr oben, Schnellzugriffe unten)')}
                </label>
                {onSave && (
                  <div className="flex flex-wrap items-center gap-3">
                    <button type="button" disabled={saving || !dirty} onClick={() => void saveLayout()} className={btnGhost}>
                      <Save className="h-4 w-4" aria-hidden="true" /> {t('qrWallpaper.saveLayout', 'Einstellungen an der Karte speichern')}
                    </button>
                    <span role="status" className="text-xs text-ink-faint">
                      {saveState === 'saved' && !dirty && t('qrWallpaper.saved', 'Gespeichert')}
                      {saveState === 'error' && <span className="text-danger-500">{t('qrWallpaper.saveError', 'Speichern fehlgeschlagen.')}</span>}
                      {saveState === 'idle' && dirty && t('qrWallpaper.unsaved', 'Ungespeicherte Änderungen')}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={busy} onClick={() => downloadWallpaper('image/png')} className={btnCls}>
                  <Download className="h-4 w-4" aria-hidden="true" /> {t('qrWallpaper.downloadPng', 'Hintergrund als PNG')}
                </button>
                <button type="button" disabled={busy} onClick={() => downloadWallpaper('image/jpeg')} className={btnGhost}>
                  {t('qrWallpaper.downloadJpg', 'als JPG')}
                </button>
              </div>
            </section>

            <section className="border-t border-line pt-5">
              <h4 className="mb-3 text-sm font-semibold text-ink">{t('qrWallpaper.qrOnly', 'Nur der QR-Code')}</h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-1">
                  <span className={labelCls}>{t('qrWallpaper.format', 'Dateiformat')}</span>
                  <div className="flex gap-1" role="radiogroup" aria-label={t('qrWallpaper.format', 'Dateiformat')}>
                    {FORMATS.map((f) => (
                      <button
                        key={f}
                        type="button"
                        role="radio"
                        aria-checked={qrFormat === f}
                        onClick={() => setQrFormat(f)}
                        className={`flex-1 rounded-lg border px-2 py-2 text-sm font-medium uppercase transition-colors ${qrFormat === f ? 'border-accent-500 bg-accent-500/10 text-accent-700 dark:text-accent-300' : 'border-line text-ink-muted hover:bg-raised'}`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="qrw-size" className={labelCls}>{t('qrWallpaper.size', 'Größe')}</label>
                  <select id="qrw-size" value={qrSize} disabled={qrFormat === 'svg'} onChange={(e) => setQrSize(Number(e.target.value))} className={`${fieldCls} disabled:opacity-50`}>
                    {QR_SIZES.map((s) => <option key={s} value={s}>{s} px</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <button type="button" disabled={busy} onClick={downloadQr} className={`${btnCls} w-full`}>
                    <Download className="h-4 w-4" aria-hidden="true" /> {t('qrWallpaper.downloadQr', 'QR herunterladen')}
                  </button>
                </div>
              </div>
              <p className="mt-2 text-xs text-ink-faint">
                {qrFormat === 'jpg'
                  ? t('qrWallpaper.jpgHint', 'JPG kann keine Transparenz – der QR-Code bekommt einen weißen Hintergrund.')
                  : t('qrWallpaper.transparentHint', 'PNG und SVG sind freigestellt (transparenter Hintergrund). Die QR-Farbe oben gilt auch hier.')}
              </p>
            </section>
          </div>

          <div className="order-first flex flex-col items-center gap-2 md:order-none">
            <WallpaperPreview
              canvasRef={canvasRef}
              width={width}
              height={height}
              layout={layout}
              onChange={setLayout}
              logoRatio={logo && logo.naturalWidth > 0 ? logo.naturalHeight / logo.naturalWidth : null}
              showTemplate={showTemplate}
            />
            <p className="text-xs text-ink-faint">{t('qrWallpaper.preview', 'Vorschau')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LayoutSliders({ title, size, min, max, x, y, onChange }: {
  title: string;
  size: number;
  min: number;
  max: number;
  x: number;
  y: number;
  onChange: (p: { size?: number; x?: number; y?: number }) => void;
}) {
  const { t } = useAppTranslation();
  const row = (label: string, value: number, lo: number, hi: number, key: 'size' | 'x' | 'y') => (
    <label className="flex items-center gap-3 text-xs text-ink-muted">
      <span className="w-20 shrink-0">{label}</span>
      <input
        type="range"
        min={Math.round(lo * 100)}
        max={Math.round(hi * 100)}
        value={Math.round(value * 100)}
        onChange={(e) => onChange({ [key]: Number(e.target.value) / 100 })}
        className="h-6 min-w-0 flex-1 accent-accent-600"
      />
      <span className="w-9 text-right tabular-nums">{Math.round(value * 100)}%</span>
    </label>
  );
  return (
    <fieldset className="space-y-1">
      <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">{title}</legend>
      {row(t('qrWallpaper.sliderSize', 'Größe'), size, min, max, 'size')}
      {row(t('qrWallpaper.sliderX', 'Horizontal'), x, 0, 1, 'x')}
      {row(t('qrWallpaper.sliderY', 'Vertikal'), y, 0, 1, 'y')}
    </fieldset>
  );
}
